import { isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isBooleanTrue, isListNotEmpty, isNumberGreaterThanOrEqualToZero } from '../utils/objectUtils';
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";

class Biomarker {
	static collectionName = 'biomarkers';

	// === EXISTING FIELDS (from Metric) ===
	name = null;
	valueType = 'STRING'; // STRING, NUMBER
	valueUnits = null;
	normalValue = null;
	normalRangeLow = null;
	normalRangeHigh = null;
	helpText = null;

	// === NEW FIELDS ===

	// Identity & coding
	slug = null;
	shortName = null;
	description = null;
	loincCode = null;
	loincName = null;

	// Multi-lab mapping (like VariantSupplyMapping for pharmacies)
	labMappings = []; // BiomarkerLabMapping[]

	// Testing frequency & schedule
	baseFrequency = 'SEMI_ANNUAL'; // enum BiomarkerFrequencies: BASELINE, ANNUAL, SEMI_ANNUAL
	isOneTime = false; // true for genetics (MTHFR, ApoE, Factor V Leiden, etc.)

	// Conditional testing (e.g., ANA Pattern only if ANA Screen positive)
	isConditional = false;
	conditionalOnBiomarkerId = null;
	conditionalOnResult = null;

	// Specimen & collection requirements
	specimenType = null; // enum TestSpecimens: BLOOD, URINE, etc.
	requiresFasting = false;
	fastingHours = null;
	requiresMorningDraw = false;
	morningDrawBefore = null;
	preDrawInstructions = null;

	// Assay specifications (general notes; lab-specific details in labMappings[])
	assayNotes = null;

	// Sex applicability
	sexApplicability = 'ALL'; // enum SexApplicability: ALL, MALE, FEMALE

	// Standard reference range text (complements normalRangeLow/normalRangeHigh)
	normalRangeText = null;

	// Optimal reference ranges (age/sex-stratified)
	optimalRanges = []; // OptimalRange[]

	// Health category assignment
	healthCategoryIds = [];

	// Derived/calculated biomarkers
	isDerived = false;
	derivedFormula = null;
	derivedSourceBiomarkerIds = [];
	derivedFormulaLabel = null;

	// Health category
	healthCategory = null; // enum HealthCategories

	// Quarterly treatment triggers (Product SKUs that cause this biomarker to appear in Q3 panels)
	quarterlyTriggerProductSkus = [];

	// Lab codes (for direct LabCorp integration and reference)
	labCorpCode = null;
	labCorpPanelCode = null; // LabCorp panel code if part of a panel (e.g., '322000' for CMP)

	// Junction integration
	junctionMarkerId = null;

	// Display & content
	displayOrder = 0;
	imageRefId = null;
	tags = [];
	whyItMatters = null;
	howToImprove = null;

	// Notes from requirements doc
	clinicalNotes = null;

	status = 'ACTIVE'; // enum: ACTIVE, INACTIVE

	constructor(biomarker) {
		if (isObjectNotEmpty(biomarker)) {
			// Existing fields
			if (isStringNotEmpty(biomarker.name)) { this.name = biomarker.name; }
			if (isStringNotEmpty(biomarker.valueType)) { this.valueType = biomarker.valueType; }
			if (isStringNotEmpty(biomarker.valueUnits)) { this.valueUnits = biomarker.valueUnits; }
			if (isNotNullOrUndefined(biomarker.normalValue)) { this.normalValue = biomarker.normalValue; }
			if (isNotNullOrUndefined(biomarker.normalRangeLow)) { this.normalRangeLow = biomarker.normalRangeLow; }
			if (isNotNullOrUndefined(biomarker.normalRangeHigh)) { this.normalRangeHigh = biomarker.normalRangeHigh; }
			if (isStringNotEmpty(biomarker.helpText)) { this.helpText = biomarker.helpText; }

			// Identity & coding
			if (isStringNotEmpty(biomarker.slug)) { this.slug = biomarker.slug; }
			if (isStringNotEmpty(biomarker.shortName)) { this.shortName = biomarker.shortName; }
			if (isStringNotEmpty(biomarker.description)) { this.description = biomarker.description; }
			if (isStringNotEmpty(biomarker.loincCode)) { this.loincCode = biomarker.loincCode; }
			if (isStringNotEmpty(biomarker.loincName)) { this.loincName = biomarker.loincName; }

			// Multi-lab mapping
			if (isListNotEmpty(biomarker.labMappings)) { this.labMappings = biomarker.labMappings; }

			// Testing frequency & schedule
			if (isStringNotEmpty(biomarker.baseFrequency)) { this.baseFrequency = biomarker.baseFrequency; }
			if (isBooleanTrue(biomarker.isOneTime)) { this.isOneTime = true; }

			// Conditional testing
			if (isBooleanTrue(biomarker.isConditional)) { this.isConditional = true; }
			if (isStringNotEmpty(biomarker.conditionalOnBiomarkerId)) { this.conditionalOnBiomarkerId = biomarker.conditionalOnBiomarkerId; }
			if (isStringNotEmpty(biomarker.conditionalOnResult)) { this.conditionalOnResult = biomarker.conditionalOnResult; }

			// Specimen & collection
			if (isStringNotEmpty(biomarker.specimenType)) { this.specimenType = biomarker.specimenType; }
			if (isBooleanTrue(biomarker.requiresFasting)) { this.requiresFasting = true; }
			if (isNotNullOrUndefined(biomarker.fastingHours)) { this.fastingHours = biomarker.fastingHours; }
			if (isBooleanTrue(biomarker.requiresMorningDraw)) { this.requiresMorningDraw = true; }
			if (isStringNotEmpty(biomarker.morningDrawBefore)) { this.morningDrawBefore = biomarker.morningDrawBefore; }
			if (isStringNotEmpty(biomarker.preDrawInstructions)) { this.preDrawInstructions = biomarker.preDrawInstructions; }

			// Assay
			if (isStringNotEmpty(biomarker.assayNotes)) { this.assayNotes = biomarker.assayNotes; }

			// Sex applicability
			if (isStringNotEmpty(biomarker.sexApplicability)) { this.sexApplicability = biomarker.sexApplicability; }

			// Standard reference range text
			if (isStringNotEmpty(biomarker.normalRangeText)) { this.normalRangeText = biomarker.normalRangeText; }

			// Optimal ranges
			if (isListNotEmpty(biomarker.optimalRanges)) { this.optimalRanges = biomarker.optimalRanges; }

			// Health categories
			if (isListNotEmpty(biomarker.healthCategoryIds)) { this.healthCategoryIds = biomarker.healthCategoryIds; }

			// Derived/calculated
			if (isBooleanTrue(biomarker.isDerived)) { this.isDerived = true; }
			if (isStringNotEmpty(biomarker.derivedFormula)) { this.derivedFormula = biomarker.derivedFormula; }
			if (isListNotEmpty(biomarker.derivedSourceBiomarkerIds)) { this.derivedSourceBiomarkerIds = biomarker.derivedSourceBiomarkerIds; }
			if (isStringNotEmpty(biomarker.derivedFormulaLabel)) { this.derivedFormulaLabel = biomarker.derivedFormulaLabel; }

			// Health category
			if (isStringNotEmpty(biomarker.healthCategory)) { this.healthCategory = biomarker.healthCategory; }

			// Quarterly treatment triggers
			if (isListNotEmpty(biomarker.quarterlyTriggerProductSkus)) { this.quarterlyTriggerProductSkus = biomarker.quarterlyTriggerProductSkus; }

			// Lab codes
			if (isStringNotEmpty(biomarker.labCorpCode)) { this.labCorpCode = biomarker.labCorpCode; }
			if (isStringNotEmpty(biomarker.labCorpPanelCode)) { this.labCorpPanelCode = biomarker.labCorpPanelCode; }

			// Junction integration
			if (isStringNotEmpty(biomarker.junctionMarkerId)) { this.junctionMarkerId = biomarker.junctionMarkerId; }

			// Display & content
			if (isNumberGreaterThanOrEqualToZero(biomarker.displayOrder)) { this.displayOrder = biomarker.displayOrder; }
			if (isStringNotEmpty(biomarker.imageRefId)) { this.imageRefId = biomarker.imageRefId; }
			if (isListNotEmpty(biomarker.tags)) { this.tags = biomarker.tags; }
			if (isStringNotEmpty(biomarker.whyItMatters)) { this.whyItMatters = biomarker.whyItMatters; }
			if (isStringNotEmpty(biomarker.howToImprove)) { this.howToImprove = biomarker.howToImprove; }

			// Clinical notes
			if (isStringNotEmpty(biomarker.clinicalNotes)) { this.clinicalNotes = biomarker.clinicalNotes; }

			// Status
			if (isStringNotEmpty(biomarker.status)) { this.status = biomarker.status; }
		}
	}

	toDto = () => {
		return {
			name: this.name,
			valueType: this.valueType,
			valueUnits: this.valueUnits,
			normalValue: this.normalValue,
			normalRangeLow: this.normalRangeLow,
			normalRangeHigh: this.normalRangeHigh,
			helpText: this.helpText,

			slug: this.slug,
			shortName: this.shortName,
			description: this.description,
			loincCode: this.loincCode,
			loincName: this.loincName,

			labMappings: this.labMappings,

			baseFrequency: this.baseFrequency,
			isOneTime: this.isOneTime,

			isConditional: this.isConditional,
			conditionalOnBiomarkerId: this.conditionalOnBiomarkerId,
			conditionalOnResult: this.conditionalOnResult,

			specimenType: this.specimenType,
			requiresFasting: this.requiresFasting,
			fastingHours: this.fastingHours,
			requiresMorningDraw: this.requiresMorningDraw,
			morningDrawBefore: this.morningDrawBefore,
			preDrawInstructions: this.preDrawInstructions,

			assayNotes: this.assayNotes,

			sexApplicability: this.sexApplicability,

			normalRangeText: this.normalRangeText,

			optimalRanges: this.optimalRanges,

			healthCategoryIds: this.healthCategoryIds,

			isDerived: this.isDerived,
			derivedFormula: this.derivedFormula,
			derivedSourceBiomarkerIds: this.derivedSourceBiomarkerIds,
			derivedFormulaLabel: this.derivedFormulaLabel,

			healthCategory: this.healthCategory,

			quarterlyTriggerProductSkus: this.quarterlyTriggerProductSkus,

			labCorpCode: this.labCorpCode,
			labCorpPanelCode: this.labCorpPanelCode,

			junctionMarkerId: this.junctionMarkerId,

			displayOrder: this.displayOrder,
			imageRefId: this.imageRefId,
			tags: this.tags,
			whyItMatters: this.whyItMatters,
			howToImprove: this.howToImprove,

			clinicalNotes: this.clinicalNotes,

			status: this.status
		};
	}

	isValid = () => {
		if (isStringEmpty(this.name)) {
			throw new Error('name is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			return await create(Biomarker.collectionName, this.toDto());
		}
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(Biomarker.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Biomarker.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedBiomarker) => {
		const updatedObj = new Biomarker(updatedBiomarker);
		if (updatedObj.isValid() && id === updatedBiomarker.id) {
			return await update(Biomarker.collectionName, id, updatedObj.toDto());
		} else {
			throw new Error('id and id field of the updated biomarker must match.')
		}
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(Biomarker.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getById = async (id) => {
		return await getById(Biomarker.collectionName, id);
	}

	static getAll = async () => {
		return await getAll(Biomarker.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer) => {
		return await getBySingleQuery(Biomarker.collectionName, fieldName, fieldValue, comparer);
	}

	static getByIds = async (ids) => {
		return await getByIdQuery(Biomarker.collectionName, ids, 'in');
	}

	static getByHealthCategoryId = async (healthCategoryId) => {
		return await getBySingleQuery(Biomarker.collectionName, 'healthCategoryIds', healthCategoryId, 'array-contains');
	}

	static getCount = async (queryParameters) => {
		const { healthCategoryId, baseFrequency, status, start, end } = queryParameters;

		let queryConstraints = [];

		if (isStringNotEmpty(healthCategoryId)) {
			queryConstraints.push(where('healthCategoryIds', 'array-contains', healthCategoryId));
		}

		if (isStringNotEmpty(baseFrequency)) {
			queryConstraints.push(where('baseFrequency', '==', baseFrequency));
		}

		if (isStringNotEmpty(status)) {
			queryConstraints.push(where('status', '==', status));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = query(collection(db, Biomarker.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'name') => {
		const { healthCategoryId, baseFrequency, status, start, end } = queryParameters;

		let queryConstraints = [];

		if (isStringNotEmpty(healthCategoryId)) {
			queryConstraints.push(where('healthCategoryIds', 'array-contains', healthCategoryId));
		}

		if (isStringNotEmpty(baseFrequency)) {
			queryConstraints.push(where('baseFrequency', '==', baseFrequency));
		}

		if (isStringNotEmpty(status)) {
			queryConstraints.push(where('status', '==', status));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, Biomarker.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, Biomarker.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, Biomarker.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;
			let totalPages = 0;

			let totalCount = await this.getCount(queryParameters);
			totalPages = Math.ceil(totalCount / 10000.0);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				let list = await this.getPaginated(queryParameters, 10000, null, next, 'name');

				allRecords = [...allRecords, ...list];

				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default Biomarker;
